import React, { useState, useEffect } from 'react';
import { Zap, Phone, MapPin, Clock, User, AlertCircle, CheckCircle, PlayCircle, RefreshCw, Loader2, Bell } from 'lucide-react';
import { dispatchAPI, jobAPI } from '../api/client';
import apiClient from '../api/client';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import MapView from '../components/MapView';

export default function DispatchCenter() {
  const [overview, setOverview] = useState(null);
  const [mapData, setMapData] = useState({ jobs: [], center: { lat: 39.5, lng: -98.35, zoom: 4 } });
  const [loading, setLoading] = useState(true);
  const [assigningJob, setAssigningJob] = useState(null);

  useEffect(() => {
    loadOverview();
    loadMap();
    const interval = setInterval(() => { loadOverview(); loadMap(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOverview = async () => {
    try {
      const data = await dispatchAPI.getOverview();
      setOverview(data);
    } catch (e) {
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  };

  const loadMap = async () => {
    try {
      const res = await apiClient.get('/api/dispatch/map');
      setMapData(res.data || { jobs: [], center: { lat: 39.5, lng: -98.35, zoom: 4 } });
    } catch (e) {
      // soft fail — map is non-critical
    }
  };

  const assignTechnician = async (jobId, technicianId) => {
    try {
      await jobAPI.update(jobId, { technician_id: technicianId, status: 'scheduled' });
      toast.success('Technician assigned!');
      setAssigningJob(null);
      loadOverview();
    } catch (e) {
      toast.error('Failed to assign');
    }
  };

  const startJob = async (jobId) => {
    try {
      await jobAPI.update(jobId, { status: 'in_progress' });
      toast.success('Job started');
      loadOverview();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const completeJob = async (jobId) => {
    try {
      await jobAPI.update(jobId, { status: 'completed' });
      toast.success('Job completed!');
      loadOverview();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const advanceJob = async (jobId) => {
    try {
      const result = await jobAPI.advance(jobId);
      const sx = result.side_effects || {};
      const extras = [];
      if (sx.invoice_number) extras.push(`Invoice ${sx.invoice_number}`);
      if (sx.sms_sent_to) extras.push(`SMS → ${sx.sms_sent_to}`);
      if (sx.archived_to_customer) extras.push('Archived to customer');
      toast.success(
        `${result.from_status.replace('_', ' ')} → ${result.to_status.replace('_', ' ')}` +
          (extras.length ? ` · ${extras.join(' · ')}` : ''),
      );
      loadOverview();
      loadMap();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not advance job');
    }
  };

  const formatTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  const { pending_jobs = [], active_jobs = [], technicians = [], active_calls = [], upcoming_reminders = [], counts = {} } = overview || {};

  return (
    <div className="space-y-6 fade-in" data-testid="dispatch-center-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Zap className="w-8 h-8 text-yellow-500" />
            Dispatch Center
          </h1>
          <p className="text-muted-foreground">Real-time job dispatching and technician management</p>
        </div>
        <Button onClick={loadOverview} variant="outline" data-testid="refresh-button">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Live Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dispatch-overview">
        <Card className="stat-orange border" data-testid="pending-jobs-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Pending</p><p className="text-3xl font-bold text-orange-500" data-testid="pending-jobs-count">{counts.pending || 0}</p></div>
              <AlertCircle className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-purple border" data-testid="active-jobs-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Active</p><p className="text-3xl font-bold text-purple-500" data-testid="active-jobs-count">{counts.active || 0}</p></div>
              <PlayCircle className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-cyan border" data-testid="active-calls-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Live Calls</p><p className="text-3xl font-bold text-cyan-500" data-testid="active-calls-count">{counts.active_calls || 0}</p></div>
              <Phone className="w-8 h-8 text-cyan-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-pink border" data-testid="technicians-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Technicians</p><p className="text-3xl font-bold text-pink-500" data-testid="technicians-count">{counts.technicians || 0}</p></div>
              <User className="w-8 h-8 text-pink-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-blue border" data-testid="upcoming-reminders-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Reminders</p><p className="text-3xl font-bold text-blue-500" data-testid="reminders-count">{counts.reminders || 0}</p></div>
              <Bell className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Map */}
      <Card data-testid="dispatch-map-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-500" />
              Live Job Map
              <Badge variant="outline" className="ml-2 text-[10px]">{mapData.jobs?.length || 0} active</Badge>
            </h2>
            <p className="text-xs text-muted-foreground">Free maps via OpenStreetMap</p>
          </div>
          <MapView pins={mapData.jobs || []} center={mapData.center} height={420} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pending Jobs - 2 cols */}
        <div className="lg:col-span-2 space-y-3">
          <Card className="border-orange-500/30">
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Pending Jobs (Need Assignment)
              </h3>
              {pending_jobs.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6">All caught up! No pending jobs.</p>
              ) : (
                <div className="space-y-2">
                  {pending_jobs.map(job => (
                    <div key={job.id} className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold">{job.title}</p>
                            {job.priority === 'urgent' && <Badge className="bg-red-500/20 text-red-500 border-red-500/30">🚨 URGENT</Badge>}
                            {job.priority === 'high' && <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">HIGH</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {job.service_type} {job.location && `• ${job.location}`}
                          </p>
                          {job.description && <p className="text-xs mt-1">{job.description}</p>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {assigningJob === job.id ? (
                            <Select onValueChange={(techId) => assignTechnician(job.id, techId)}>
                              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Pick tech..." /></SelectTrigger>
                              <SelectContent>
                                {technicians.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Button size="sm" onClick={() => setAssigningJob(job.id)} className="btn-gradient-info" data-testid={`assign-${job.id}`}>
                              <User className="w-3 h-3 mr-1" /> Assign
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => advanceJob(job.id)}
                            className="btn-gradient-success"
                            data-testid={`dispatch-advance-${job.id}`}
                          >
                            <PlayCircle className="w-3 h-3 mr-1" /> Progress →
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-purple-500/30">
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-purple-500" />
                Active Jobs (In Progress)
              </h3>
              {active_jobs.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6">No active jobs right now</p>
              ) : (
                <div className="space-y-2">
                  {active_jobs.map(job => {
                    const tech = technicians.find(t => t.id === job.technician_id);
                    return (
                      <div key={job.id} className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold">{job.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {tech ? `${tech.first_name} ${tech.last_name}` : 'Unassigned'} {job.location && `• ${job.location}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => advanceJob(job.id)} className="btn-gradient-info" data-testid={`dispatch-advance-active-${job.id}`}>
                              <PlayCircle className="w-3 h-3 mr-1" /> Progress →
                            </Button>
                            <Button size="sm" onClick={() => completeJob(job.id)} className="btn-gradient-success">
                              <CheckCircle className="w-3 h-3 mr-1" /> Complete
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          {/* Technicians */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Available Technicians
              </h3>
              {technicians.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No technicians available</p>
              ) : (
                <div className="space-y-2">
                  {technicians.map(t => {
                    const activeJob = active_jobs.find(j => j.technician_id === t.id);
                    return (
                      <div key={t.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                        {t.avatar_url ? (
                          <img
                            src={t.avatar_url}
                            alt={`${t.first_name} ${t.last_name}`}
                            className="w-8 h-8 rounded-full object-cover border border-primary/30"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {t.first_name?.[0]}{t.last_name?.[0]}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{t.first_name} {t.last_name}</p>
                          {activeJob ? (
                            <p className="text-[10px] text-purple-500">On job</p>
                          ) : (
                            <p className="text-[10px] text-green-500">Available</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Reminders */}
          {upcoming_reminders.length > 0 && (
            <Card className="border-blue-500/30">
              <CardContent className="p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-blue-500" /> Upcoming Reminders
                </h3>
                <div className="space-y-2">
                  {upcoming_reminders.slice(0, 5).map(r => (
                    <div key={r.id} className="p-2 bg-blue-500/5 border border-blue-500/20 rounded text-sm">
                      <p className="font-medium">{r.title}</p>
                      {r.customer_name && <p className="text-xs text-muted-foreground">{r.customer_name} • {r.customer_phone}</p>}
                      <p className="text-xs text-blue-500 mt-1">⏰ {new Date(r.remind_date).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Live Calls */}
          {active_calls.length > 0 && (
            <Card className="border-cyan-500/30">
              <CardContent className="p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2 text-cyan-500">
                  <Phone className="w-4 h-4 animate-pulse" /> Active Calls
                </h3>
                <div className="space-y-2">
                  {active_calls.map(c => (
                    <div key={c.id} className="p-2 bg-cyan-500/5 border border-cyan-500/20 rounded text-sm">
                      <p className="font-medium">{c.customer_name || c.from_number}</p>
                      <p className="text-xs text-muted-foreground">{c.from_number}</p>
                      <Badge variant="outline" className="mt-1 text-[10px]">{c.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
